import { useEffect, useCallback } from 'react'
import { useAchievementStore } from '@/store/useAchievementStore'
import { useAuthStore } from '@/store/useAuthStore'
import { useTaskStore } from '@/store/useTaskStore'
import { useFamilyStore } from '@/store/useFamilyStore'
import { useNotificationStore } from '@/store/useNotificationStore'
import { checkAchievementCriteria } from '@/lib/achievements'
import { showAchievementNotification } from '@/lib/notifications'

export function useAchievements() {
  const currentUserId = useAuthStore((state) => state.currentUserId)
  const {
    achievements,
    achievementProgress,
    fetchAchievements,
    fetchAchievementProgress,
    checkAndAwardAchievement,
  } = useAchievementStore()
  const completions = useTaskStore((state) => state.completions)
  const members = useFamilyStore((state) => state.members)
  const { preferences, createNotification } = useNotificationStore()

  // Fetch achievements and progress on mount
  useEffect(() => {
    fetchAchievements()
  }, [fetchAchievements])

  useEffect(() => {
    if (currentUserId) {
      fetchAchievementProgress(currentUserId)
    }
  }, [currentUserId, fetchAchievementProgress])

  // Check achievements after task completion
  const checkAchievementsForUser = useCallback(
    async (userId: string) => {
      const member = members.find((m) => m.id === userId)
      if (!member) return

      const userCompletions = completions.filter((c) => c.completed_by === userId)

      // Check each achievement
      for (const achievement of achievements) {
        const { met } = checkAchievementCriteria(achievement.unlock_criteria, {
          taskCompletions: userCompletions,
          member,
        })

        if (met) {
          const awarded = await checkAndAwardAchievement(userId, achievement.name)

          if (awarded) {
            // Create in-app notification
            createNotification(
              userId,
              'achievement',
              `Achievement Unlocked! ${achievement.icon}`,
              `${achievement.display_name} - You earned ${achievement.points} points!`,
              { achievement_id: achievement.id }
            )

            // Show browser notification if enabled
            if (preferences?.browser_notifications && preferences?.achievement_unlocks) {
              showAchievementNotification(
                achievement.display_name,
                achievement.icon,
                achievement.points
              )
            }
          }
        }
      }
    },
    [achievements, members, completions, checkAndAwardAchievement, createNotification, preferences]
  )

  // Auto-check achievements when completions change
  useEffect(() => {
    if (currentUserId && achievements.length > 0) {
      checkAchievementsForUser(currentUserId)
    }
  }, [currentUserId, completions.length, achievements.length, checkAchievementsForUser])

  return {
    achievements,
    achievementProgress,
    checkAchievementsForUser,
  }
}
